import type { TeamOperation } from "../../api/proTeams";
import { useState } from "react";
import { Button } from "../../ui";
import { failureMessage } from "./state";

const stages: Record<string, string> = {
  create_request: "Creating the team",
  confirm_team: "Reading the created team",
  validate_team: "Checking team details and administrator",
  confirmed: "Team confirmed",
};

export function OperationDetails({ operation, onRecover, recovering = false }: {
  operation: TeamOperation;
  onRecover?: (botId: string) => Promise<string>;
  recovering?: boolean;
}) {
  const [recovery, setRecovery] = useState<{ operationId: string; pending?: boolean; message?: string; error?: string }>();
  const currentRecovery = recovery?.operationId === operation.operation_id ? recovery : undefined;
  const d = operation.diagnostics;
  const failed = operation.status === "failed" || operation.status === "unknown";
  const canRecover = !!onRecover && failed && ["bot_quarantined", "outcome_unknown", "remote_outcome_unknown"].includes(operation.code ?? "");
  async function recover() {
    if (!onRecover || recovering || currentRecovery?.pending) return;
    const operationId = operation.operation_id;
    setRecovery({ operationId, pending: true });
    try {
      const message = await onRecover(operation.bot_id);
      setRecovery({ operationId, message });
    } catch (error) {
      setRecovery({ operationId, error: failureMessage(error) });
    }
  }
  const message = operation.message || operation.code?.replaceAll("_", " ");
  const facts: Array<[string, string | number | undefined | null]> = [
    ["Operation ID", operation.operation_id],
    ["Bot ID", operation.bot_id],
    ["Team ID", operation.team_id],
    ["Error code", operation.code],
    ["GC result", operation.gc_result],
  ];
  if (d) facts.push(
    ["Stage", stages[d.stage] ?? d.stage],
    ["Reason", d.reason],
    ["Error type", d.error_type],
    ["Creation acknowledged", d.gc_acknowledged ? "Yes" : "No"],
    ["Creation duration", `${d.create_elapsed_ms} ms`],
    ["Confirmation duration", `${d.confirmation_elapsed_ms} ms`],
    ["Time remaining before confirmation", d.confirmation_budget_ms == null ? undefined : `${d.confirmation_budget_ms} ms`],
    ["Expected team ID", d.expected_team_id],
    ["Observed team ID", d.observed_team_id],
    ["Observed roster size", d.stage === "validate_team" || d.stage === "confirmed" ? d.observed_member_count : undefined],
  );

  return (
    <div className="min-w-0 space-y-2 break-words text-xs text-ink-dim">
      <p>{message || "Accepted requests are tracked until the API reports their outcome."}</p>
      {operation.action === "invite" && operation.invitation_pending && (
        <p>Acceptance has not been observed. This does not block the bot: you can invite other players or send this player another invitation. Sync updates confirmed membership.</p>
      )}
      {failed && d?.detail && (
        <p className="font-medium text-warn">{d.detail}</p>
      )}
      {failed && d?.next_action && (
        <p className="rounded-md border border-warn/30 bg-warn/5 p-3 text-ink">
          <span className="font-medium">Next step: </span>{d.next_action}
        </p>
      )}
      {(canRecover || currentRecovery) && (
        <div className="space-y-2 rounded-md border border-warn/30 p-3">
          {canRecover && <>
            <p>Reconnect this bot and clear its quarantine. Active lobbies and series must finish first. Unconfirmed team operations will still need a sync or import.</p>
            <Button onClick={() => void recover()} disabled={recovering || currentRecovery?.pending || !!currentRecovery?.message}>
              {currentRecovery?.pending ? "Recovering bot…" : currentRecovery?.message ? "Quarantine cleared" : "Clear bot quarantine"}
            </Button>
          </>}
          {currentRecovery?.message && <p role="status" className="text-ok">{currentRecovery.message}</p>}
          {currentRecovery?.error && <p role="alert" className="text-bad">{currentRecovery.error}</p>}
        </div>
      )}
      {(failed || d) && (
        <details className="rounded-md border border-line p-3">
          <summary className="cursor-pointer font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
            Technical details
          </summary>
          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            {facts.filter(([, value]) => value != null && value !== "").map(([label, value]) => (
              <div key={label} className="contents">
                <dt>{label}</dt><dd className="min-w-0 break-all text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          {!!d?.mismatches?.length && (
            <div className="mt-4 space-y-3 border-t border-line pt-3">
              <p className="font-medium text-ink">Mismatched fields</p>
              {d.mismatches.map((mismatch, index) => (
                <div key={`${mismatch.field}-${index}`} className="space-y-1">
                  <p className="font-medium text-ink">{mismatch.field.replaceAll("_", " ")}</p>
                  <p>Expected: <span className="break-all">{mismatch.expected || "(empty)"}</span></p>
                  <p>Observed: <span className="break-all">{mismatch.observed || "(empty)"}</span></p>
                </div>
              ))}
            </div>
          )}
        </details>
      )}
    </div>
  );
}
